import React, { useEffect, useState } from 'react';
import { Form, FormInstance } from 'antd';
import { FormListFieldData } from 'antd/es/form/FormList';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { AddButton, RemoveButton } from '../CustomButtons';
import { IEmailRecipient, ISelect, NotificationType } from '../../interfaces';
import { useUsersSelect } from '../../hooks';
import {
    addNotification,
    apiMessagesDeleteMessageFormFieldName,
    apiMessagesGetFormRulesAndEmailAndBlank,
    apiMessagesGetFormRulesFieldBlank2,
    getUserEmailRecipientById,
} from '../../middleware';
import { tB, tF, tI, tNM } from '../../translate';
import { Catcher } from '../Catcher';
import { FormSelect, InputDebounce } from '../Forms';
import Styles from './m_styles.less';

type IEmailRecipientsEdit = {
    name: string;
    form: FormInstance;
};

export const EmailRecipientsEdit: React.FC<IEmailRecipientsEdit> = ({ name, form }: IEmailRecipientsEdit) => {
    const [exceptionIds, setExceptionIds] = useState<Array<string | undefined>>([]);

    const usersList = useUsersSelect();

    const checkValid = (): void => {
        form.validateFields();
    };

    const calcExceptionIds = () => {
        const usedUserNamesList =
            form
                .getFieldValue(name)
                ?.map((i: IEmailRecipient | undefined) => (i ? i.name : undefined))
                .filter((i: string | undefined) => !!i) || [];

        const exceptionUserIds: Array<string | undefined> = usersList
            .filter((u: ISelect) => u?.name && usedUserNamesList.includes(u.name))
            .map((u: ISelect) => u.id);

        setExceptionIds(exceptionUserIds);
    };

    useEffect(() => {
        setTimeout(() => {
            calcExceptionIds();
        }, 2500);
    }, [usersList?.length]);

    return (
        <Catcher>
            <Form.List name={name}>
                {(fields: FormListFieldData[], { add, remove }): JSX.Element => {
                    const addUser = async (id: string): Promise<void> => {
                        const user = await getUserEmailRecipientById(id);
                        if (user.name || user.email) {
                            add(user);
                        } else {
                            addNotification({
                                type: NotificationType.WARNING,
                                message: tNM('Not Found'),
                                description: `${tI('User id')} - "${id}" ${tI('not found')}`,
                                duration: 5,
                                isTranslated: true,
                            });
                        }
                    };
                    const onChangeUserSelector = (id: string | string[]): void => {
                        addUser(typeof id === 'string' ? id : id[0]).then(() => calcExceptionIds());
                    };
                    return (
                        <div className={Styles.place}>
                            <div className={Styles.toolBar}>
                                <div className={Styles.selector}>
                                    <FormSelect
                                        fieldName={'tempUser'}
                                        useSelectHook={useUsersSelect}
                                        onChange={onChangeUserSelector}
                                        placeholder={tB('Add user')}
                                        exceptionIds={exceptionIds}
                                    />
                                </div>

                                <AddButton
                                    className={Styles.button}
                                    title={'New recipient'}
                                    icon={<EditOutlined />}
                                    onClick={() => {
                                        add();
                                    }}
                                />
                            </div>
                            {fields.map((field: FormListFieldData, recordIndex: number) => {
                                const deleteApiNameErrorMessage = () => {
                                    apiMessagesDeleteMessageFormFieldName(`${name}.${field.name}.name`);
                                    calcExceptionIds();
                                    checkValid();
                                };
                                const deleteApiEmailErrorMessage = () => {
                                    apiMessagesDeleteMessageFormFieldName(`${name}.${field.name}.email`);
                                    checkValid();
                                };

                                return (
                                    <div key={field.key} className={Styles.record}>
                                        <div className={Styles.number}>{recordIndex + 1}.</div>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'name']}
                                            key={'name'}
                                            className={Styles.field}
                                            rules={apiMessagesGetFormRulesFieldBlank2}
                                        >
                                            <InputDebounce
                                                placeholder={tF('Name')}
                                                onChange={deleteApiNameErrorMessage}
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            {...field}
                                            name={[field.name, 'email']}
                                            key={'email'}
                                            className={Styles.field}
                                            rules={apiMessagesGetFormRulesAndEmailAndBlank}
                                        >
                                            <InputDebounce
                                                placeholder={tF('Email')}
                                                onChange={deleteApiEmailErrorMessage}
                                            />
                                        </Form.Item>
                                        <RemoveButton
                                            key={'Delete'}
                                            isHiddenTitle
                                            onClick={() => {
                                                remove(field.name);
                                                calcExceptionIds();
                                            }}
                                            icon={<DeleteOutlined />}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    );
                }}
            </Form.List>
        </Catcher>
    );
};
